import { BehaviorSubject } from 'rxjs'
import { ITrack } from '../../Interface/track'
import { TracksService } from '../../services/tracks.service'
import { PlayerCore } from './PlayerCore'
import { PlaylistManager } from './PlaylistManager'
import { SubscriberType, SubscribingObject } from './SubscribingObject'
import { PlayerLogicTrackType, RepeatMode } from './Types'

export type PlayerLogicEvents = 'ended' | 'progress' | 'timeupdate' | 'pause' | 'play' | 'change-progress'

export enum PlayStatus {
  Stopped,
  Playing,
  Paused
}

type PlayerEventBusEventsType = 'plaiyng-started'

class PlayerEventBus {
  lsKey = 'player_events_playing_started'

  constructor() {
    window.addEventListener('storage', e => {
      if (e.key === this.lsKey) {
        this.events.triggerEvent('plaiyng-started')
      }
    })
  }

  private events: SubscribingObject<PlayerEventBusEventsType> = new SubscribingObject<PlayerEventBusEventsType>()

  callMeWhenPlaingStartedOnOtherTab(cb: () => void) {
    this.events.addSubscriber('plaiyng-started', cb)
  }

  notifyOtherTabsAboutMyPlaying() {
    localStorage.setItem(this.lsKey, Math.random().toString())
  }
}

export class PlayerLogic {
  private readonly SECONDS_BEFORE_PREV_TRACK_ALLOWED = 3

  get isPlaying() {
    return this.status === PlayStatus.Playing
  }

  private playerEventBus: PlayerEventBus
  private trackDataValue: any // data of beat, playing in core
  public currentTrack: PlayerLogicTrackType<any> | null = null
  public prevTrack: PlayerLogicTrackType<any> | null = null
  public initialPromoTrack = true

  private _repeatMode$ = new BehaviorSubject<RepeatMode>('off')
  private _isShuffleActive$ = new BehaviorSubject<boolean>(false)

  get repeatMode$() {
    return this._repeatMode$.asObservable()
  }

  get isShuffleActive$() {
    return this._isShuffleActive$.asObservable()
  }

  public get trackData() {
    return this.trackDataValue
  }

  public status: PlayStatus = PlayStatus.Stopped

  private eventsObject: SubscribingObject<PlayerLogicEvents> = new SubscribingObject<PlayerLogicEvents>()

  playingProgressPercent = 0
  loadingProgressPercent = 0

  constructor(
    private playerCore: PlayerCore,
    private playlistManager: PlaylistManager,
    private tracksService: TracksService
  ) {
    playerCore.bindEvent('timeupdate', () => {
      this.playingProgressPercent = playerCore.currentPositionInPercent()
      this.eventsObject.triggerEvent('timeupdate', {
        currentPosition: this.playingProgressPercent
      })
    })

    playerCore.bindEvent('ended', () => {
      this.eventsObject.triggerEvent('ended', {})
    })

    playerCore.bindEvent('progress', () => {
      this.loadingProgressPercent = playerCore.getBufferedPercent()

      this.eventsObject.triggerEvent('progress', {
        currentLoadPosition: this.loadingProgressPercent
      })
    })

    this.playerEventBus = new PlayerEventBus()
    this.playerEventBus.callMeWhenPlaingStartedOnOtherTab(this.pause.bind(this))
  }

  isMyTrack(id: number | string, type: string) {
    return this.currentTrack?.id === id && this.currentTrack?.type === type
  }

  subscribe(eventName: PlayerLogicEvents, subscriber: SubscriberType) {
    if ((eventName === 'play' && this.isPlaying) || (eventName === 'pause' && !this.isPlaying && !!this.currentTrack)) {
      subscriber(this.currentTrack)
    }

    return this.eventsObject.addSubscriber(eventName, subscriber)
  }

  public play<T>(track: PlayerLogicTrackType<ITrack>, data: T | null = null) {
    this.status = PlayStatus.Playing
    this.currentTrack = track
    this.trackDataValue = data
    this.playerEventBus.notifyOtherTabsAboutMyPlaying()
    this.playerCore.play({ src: track.url, format: track.format })
    this.eventsObject.triggerEvent('play', { track, data })
    this.incrementTrackPlays(track)
  }

  private incrementTrackPlays(track: PlayerLogicTrackType<ITrack>) {
    // Эта проверка нужна для того чтобы не отсылалось несколько запросов одного трека и вместо одного
    // прослушивания, было больше
    if (!this.prevTrack || this.prevTrack.id !== this.currentTrack.id) {
      this.prevTrack = this.currentTrack
      this.tracksService.incrementTrackPlays(track).subscribe(
        () => {},
        error => {
          console.error(`incrementTrackPlays error: ${JSON.stringify(error)}`)
        }
      )
    }
  }

  public continuePlayAfterPause() {
    if (!this.currentTrack) {
      console.warn('No track for continue playing')
      return
    }
    this.status = PlayStatus.Playing
    this.playerEventBus.notifyOtherTabsAboutMyPlaying()
    this.playerCore.play({ src: this.currentTrack.url, format: this.currentTrack.format })
    this.eventsObject.triggerEvent('play', { track: this.currentTrack, data: this.trackData })

    // При первоначальной загрузке страницы мы подгружаем рекламный трек. Соответственно у него
    // будет статус на паузе, это означает что когда на нем кликнут, то трек будет проигрываться,
    // но количество прослушиваний (incrementTrackPlays) не отработает. Это логично, иначе если
    // человек нажал 5 раз play/pause, то зачтется 5 прослушиваний, а надо одно.
    // Но в случае рекламного трека этот трек погрузился автоматически и его никто не нажимал,
    // соответственно у него не будет прослушиваний.
    // Поэтому добавлена переменная initialPromoTrack, которая отрабатывает лишm один раз и потом всегда будет в
    // состоянии false
    if (this.initialPromoTrack) {
      this.incrementTrackPlays(this.currentTrack)
      this.initialPromoTrack = false
    }
  }

  public setTrack<T>(track: PlayerLogicTrackType<any>, data: T | null = null) {
    this.status = PlayStatus.Stopped
    this.currentTrack = track
    this.trackDataValue = data
    // trigger play event to notify subscribers for refreshing
    // todo: trigger other event?
    this.eventsObject.triggerEvent('play', { track, data })
  }

  public pause = (): void => {
    this.status = PlayStatus.Stopped
    this.playerCore.pause()
    this.eventsObject.triggerEvent('pause', this.currentTrack)
  }

  public setTrackPositionByPercent(percent: number) {
    this.playerCore.setTrackPositionByPercent(percent)
    this.eventsObject.triggerEvent('timeupdate', { percent })
  }

  public getTrackPositionInPercent(): number {
    const percent = this.playerCore.currentPositionInPercent()
    this.eventsObject.triggerEvent('change-progress', { percent })
    return percent
  }

  public setPlayList(tracks: Array<PlayerLogicTrackType<any>>) {
    this.playlistManager.setPlaylist(tracks)
  }

  public setPlayListIds(tracksIds: Array<string | number>) {
    this.playlistManager.setPlaylistTracksIds(tracksIds)
  }

  public toggleRepeatMode(): void {
    let newRepeatMode: RepeatMode
    switch (this._repeatMode$.value) {
      case 'off':
        newRepeatMode = 'all'
        break
      case 'all':
        newRepeatMode = 'one'
        break
      case 'one':
        newRepeatMode = 'off'
        break
      default:
        newRepeatMode = 'off'
    }

    this._repeatMode$.next(newRepeatMode)
    this.playlistManager.setRepeatMode(newRepeatMode)
  }

  toggleShuffle(): void {
    const newShuffleState = !this._isShuffleActive$.value
    this._isShuffleActive$.next(newShuffleState)
    this.playlistManager.setShuffleMode(newShuffleState, this.currentTrack)
  }

  public async playNext() {
    const track = await this.playlistManager.getNextTrack(this.currentTrack)

    if (!!track) {
      this.play(track, track.originalData)
    }
  }

  public async playPrev() {
    const currentTimeSeconds = this.getCurrentTimeSeconds()

    if (currentTimeSeconds >= this.SECONDS_BEFORE_PREV_TRACK_ALLOWED) {
      this.playerCore.setPositionToStart()
    } else {
      const track = await this.playlistManager.getPrevTrack(this.currentTrack)

      if (!!track) {
        this.play(track, track.originalData)
      }
    }
  }

  changeVolume(volume: number) {
    this.playerCore.setVolume(volume)
  }

  public getCurrentTimeSeconds(): number {
    return this.playerCore.getCurrentTimeInSeconds()
  }

  public getTrackDurationSeconds(): number {
    return this.playerCore.getDurationInSeconds()
  }
}
